import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { History, Pencil, Plus, Trash2 } from 'lucide-react';
import { RAW_UNIT_SUFFIX, formatIqd, fromMillidinars, type RawItemDto } from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { api, ApiError } from '@/lib/api';
import { RawItemFormDialog } from './RawItemFormDialog';
import { RawItemMovementsDrawer } from './RawItemMovementsDrawer';
import { RawStockAdjustDialog } from './RawStockAdjustDialog';

/**
 * Raw materials (Wave 3, #17) — the ingredients recipes consume on sale.
 *
 * The one rule that shapes this table: raw stock MAY BE NEGATIVE. A recipe
 * sale is never blocked by the count on paper, so a negative number here is
 * not an error state — it is the screen's most important signal, styled to be
 * impossible to miss, and corrected through the purchase/adjust dialog so the
 * fix lands in the ledger.
 */
export function RawItemsPage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const manage = can('products.manage');
  const currencySymbol = venue?.currencySymbol ?? 'IQD';

  const [items, setItems] = useState<RawItemDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<RawItemDto | null>(null);
  const [adjusting, setAdjusting] = useState<RawItemDto | null>(null);
  const [viewingMovements, setViewingMovements] = useState<RawItemDto | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await api.get<RawItemDto[]>('/raw-items'));
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('store.rawItems.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!manage) {
    return (
      <div className="flex flex-1 items-center justify-center p-10 text-sm text-content-secondary">
        {t('store.rawItems.noPermission')}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 p-4">
        <div>
          <h1 className="text-xl font-semibold text-content">{t('store.rawItems.title')}</h1>
          <p className="text-xs text-content-muted">{t('store.rawItems.subtitle')}</p>
        </div>
        <div className="flex-1" />
        <Link
          to="/cafe/products"
          className="rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
        >
          {t('cafe.products.title')}
        </Link>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" aria-hidden />
          {t('store.rawItems.addItem')}
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
        {error ? (
          <p role="alert" className="mb-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        {items === null ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-content-muted">{t('store.rawItems.empty')}</p>
        ) : (
          <RawItemsTable
            items={items}
            currencySymbol={currencySymbol}
            onEdit={setEditing}
            onAdjust={setAdjusting}
            onMovements={setViewingMovements}
            onDelete={(item) => void handleDelete(item)}
          />
        )}
      </div>

      {adding || editing ? (
        <RawItemFormDialog
          rawItem={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => void load()}
        />
      ) : null}

      {adjusting ? (
        <RawStockAdjustDialog
          rawItem={adjusting}
          onClose={() => setAdjusting(null)}
          onSaved={() => void load()}
        />
      ) : null}

      {viewingMovements ? (
        <RawItemMovementsDrawer
          rawItem={viewingMovements}
          onClose={() => setViewingMovements(null)}
        />
      ) : null}
    </div>
  );

  async function handleDelete(item: RawItemDto): Promise<void> {
    if (!window.confirm(t('store.rawItems.confirmDelete', { name: item.name }))) return;
    setError(null);
    try {
      await api.delete(`/raw-items/${item.id}`);
      await load();
    } catch (caught) {
      // `raw_item_in_use` arrives with the server's own explanation.
      setError(caught instanceof ApiError ? caught.message : t('store.rawItems.couldNotDelete'));
    }
  }
}

/**
 * The raw shelf, one row per material. Presentational and exported so the
 * table's rules — negative stock styled as danger, the low-stock badge, the
 * per-kilo/litre/piece cost label — are pinned by tests without auth or
 * fetching.
 */
export function RawItemsTable({
  items,
  currencySymbol,
  onEdit,
  onAdjust,
  onMovements,
  onDelete,
}: {
  items: readonly RawItemDto[];
  currencySymbol: string;
  onEdit: (item: RawItemDto) => void;
  onAdjust: (item: RawItemDto) => void;
  onMovements: (item: RawItemDto) => void;
  onDelete: (item: RawItemDto) => void;
}) {
  const { t } = useTranslation();
  const cellClass = 'px-3 py-2 align-middle';

  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="text-content-secondary">
          <th className={`${cellClass} text-start`}>{t('common.name')}</th>
          <th className={`${cellClass} text-start`}>{t('store.rawItems.unit')}</th>
          <th className={`${cellClass} text-end`}>{t('store.rawItems.stock')}</th>
          <th className={`${cellClass} text-end`}>{t('store.rawItems.cost')}</th>
          <th className={`${cellClass} text-start`}>{t('store.rawItems.actions')}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => {
          const negative = item.stockQuantity < 0;
          return (
            <tr
              key={item.id}
              data-negative={negative ? 'true' : 'false'}
              className={index % 2 === 0 ? 'bg-row-even' : 'bg-row-odd'}
            >
              <td className={cellClass}>
                <span className="text-content">{item.name}</span>
                {!item.isActive ? (
                  <span className="ms-2 text-xs text-content-muted">
                    ({t('store.rawItems.inactive')})
                  </span>
                ) : null}
              </td>

              <td className={`${cellClass} text-content-secondary`}>
                {t(`store.rawItems.units.${item.unit}`)}
              </td>

              <td className={`${cellClass} tabular text-end`}>
                <span className={negative ? 'font-semibold text-danger' : 'text-content'}>
                  {item.stockQuantity} {RAW_UNIT_SUFFIX[item.unit]}
                </span>
                {negative ? (
                  <span className="ms-2 rounded-full bg-danger/20 px-1.5 py-0.5 text-xs font-medium text-danger">
                    {t('store.rawItems.negative')}
                  </span>
                ) : item.lowStock ? (
                  <span className="ms-2 rounded-full bg-ending/20 px-1.5 py-0.5 text-xs font-medium text-ending">
                    {t('store.rawItems.low')}
                  </span>
                ) : null}
              </td>

              <td className={`${cellClass} tabular text-end`}>
                {item.costMd == null ? (
                  <span className="text-content-muted">—</span>
                ) : (
                  <div>
                    <div className="text-content">
                      {formatIqd(fromMillidinars(item.costMd), { currencySymbol })}
                    </div>
                    <div className="text-xs text-content-muted">
                      {t(`store.rawItems.costPerShort.${item.unit}`)}
                    </div>
                  </div>
                )}
              </td>

              <td className={cellClass}>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => onAdjust(item)}
                    className="inline-flex items-center gap-1 rounded-full border border-subtle px-2.5 py-1 text-xs text-content-secondary hover:bg-hover"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden />
                    {t('store.rawItems.adjust')}
                  </button>
                  <SmallIconButton
                    label={t('store.rawItems.movements')}
                    onClick={() => onMovements(item)}
                  >
                    <History className="h-3.5 w-3.5" aria-hidden />
                  </SmallIconButton>
                  <SmallIconButton label={t('common.edit')} onClick={() => onEdit(item)}>
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                  </SmallIconButton>
                  <SmallIconButton label={t('common.delete')} danger onClick={() => onDelete(item)}>
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </SmallIconButton>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SmallIconButton({
  children,
  label,
  danger,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`grid h-7 w-7 place-items-center rounded-control border border-subtle text-content-secondary transition-colors hover:bg-hover ${
        danger ? 'hover:text-danger' : 'hover:text-content'
      }`}
    >
      {children}
    </button>
  );
}
